import { useEventTracking } from '@services/statsig/hooks/useEventTracking';

import type { SupportTransport } from '../hooks/useSupportRequest';
import { useCheckSupportRequest, useSubmitSupportRequest } from '../store/api';
import { submissionRejection } from '../submissionRejection';
import type { SupportInteraction, SupportRequest } from '../types';

import { JotSupportFlow } from './JotSupportFlow';
import type { JotSupportDisplayProps } from './JotSupportWidget';

/** Authenticated transport mounted through the shared Front visibility wrapper. */
export function JotSupportContainer({
  replyEmail,
  pageArea = 'other',
  ...display
}: JotSupportDisplayProps & {
  replyEmail: string;
  pageArea?: SupportRequest['page_area'];
}) {
  const [submitRequest] = useSubmitSupportRequest();
  const [checkRequest] = useCheckSupportRequest();
  const { jotSupport } = useEventTracking();
  const transport: SupportTransport = {
    submit: async request => {
      const operation = submitRequest({ supportRequestCreate: request });
      try {
        return await operation.unwrap();
      } catch (error) {
        throw submissionRejection(error) ?? error;
      } finally {
        operation.reset();
      }
    },
    check: async requestId => {
      const result = checkRequest({ requestId }, false);
      try {
        return await result.unwrap();
      } finally {
        result.unsubscribe();
      }
    },
  };
  const track = (event: SupportInteraction) => {
    const actions = {
      opened: jotSupport.opened,
      closed: jotSupport.closed,
      submitted: jotSupport.submitted,
      retry_clicked: jotSupport.retryClicked,
      status_requested: jotSupport.statusRequested,
      attachment_selected: jotSupport.attachmentSelected,
    };
    actions[event]();
  };
  return (
    <JotSupportFlow
      {...display}
      transport={transport}
      replyEmail={replyEmail}
      pageArea={pageArea}
      onInteraction={track}
    />
  );
}
